/** @jsxFrag React.Fragment */

import * as React from 'react';
// eslint-disable-next-line @atlaskit/ui-styling-standard/use-compiled -- Disabled to rollout go/ui-styling-standard tooling, please resolve
import styled from 'styled-components';
import { Link } from '@helpcenter/analytics-components';
import { Button } from '@helpcenter/analytics-components/src/button';
import type { ButtonProps } from '@helpcenter/analytics-components/src/button';
import ErrorBoundary from '@helpcenter/error-boundary/src/error-boundary';
import {
	isUseHelpCenterRefresh,
	isVirtualAgentHeaderButtonEnabled,
} from '@helpcenter/feature-flags';
import { getHelpCenterAri } from '@helpcenter/meta';
import * as grid from '@helpcenter/styles/src/grid';
import * as mixins from '@helpcenter/styles/src/mixins';
import { FormattedMessage, useIntl } from 'react-intl';
import { di } from 'react-magnetic-di';
import { usePathParam } from 'react-resource-router';
import type { UserRequestsModel } from 'state/persisted/user/reducer';
import type { UserRequestsCounts } from 'state/persisted/user-requests-counts';
import { LogoMemoizedHOC } from 'view/common/logo';
import { HEADER_HEIGHT } from 'view/common/styled';
import AnalyticsContext from '@atlaskit/analytics-next/AnalyticsContext';
import Skeleton from '@atlaskit/avatar/skeleton';
import { Box } from '@atlaskit/primitives/box';
import { xcss } from '@atlaskit/primitives/xcss/xcss';
import UFOLoadHold from '@atlaskit/react-ufo/load-hold';
import { token } from '@atlaskit/tokens';

import { CsmRequestsHeaderLink } from '@atlassian/csm-requests-common/src/ui/csm-requests-header-link';
import { AvatarMenuV2 } from '@atlassian/help-center-avatar-menu';

import { isCSMHelpCenter } from '@atlassian/help-center-common-util/advanced-help-center';
import { getEnv } from '@atlassian/help-center-common-util/env';
import { getHelpCenterUrl } from '@atlassian/help-center-common-util/url';
import { Search } from '@atlassian/help-center-search';
import { CustomiseMenu } from '../customise-menu';
import { VirtualServiceAgentHeader } from '../virtual-service-agent-header';
import i18n from './messages';
interface Organisations {
	name: string;
	id: number;
}

export interface Props {
	isAnonymous?: boolean;
	isUserLoaded?: boolean;
	userLoggedIn?: boolean;
	loginUrl?: string;
	logoTextColor: 'banner' | 'theme';
	hideSearchButton?: boolean;
	isCustomizing?: boolean;
	userRequest: UserRequestsModel | UserRequestsCounts; //TODO: remove UserRequestsModel when removing isSplitRequestCountsFromUserModelEnabled
	organisations: Organisations[];
	anonymousEnabled?: boolean;
	showThemedBackground?: boolean;
	disableAllHeaderLinks?: boolean;
	headerBackgroundColor?: string;
	logoUrl?: string;
	name?: string;
}

const Header: React.FunctionComponent<Props> = ({
	isAnonymous,
	isUserLoaded,
	userLoggedIn,
	loginUrl,
	logoTextColor,
	hideSearchButton,
	anonymousEnabled,
	showThemedBackground,
	disableAllHeaderLinks,
	headerBackgroundColor,
	logoUrl,
	name,
}) => {
	di(isCSMHelpCenter);
	di(getHelpCenterAri);
	di(isVirtualAgentHeaderButtonEnabled);
	const isCSM = isCSMHelpCenter(getEnv().helpCenterType);
	const showPortalLink = isCSM ? !!logoUrl : true;
	const [portalId] = usePathParam('portalId');

	const { formatMessage } = useIntl();

	const defaultLoginUrl = `/user/login?destination=${getHelpCenterUrl().replace(/\//g, '')}`;
	const authenticatedNavLinks = React.useMemo(
		() => (
			<>
				{isVirtualAgentHeaderButtonEnabled() && !isCSM && (
					<NavItem role="listitem">
						<ErrorBoundary packageName={'viewLayoutHeaderDumb'} id={'virtualAgentHeaderButton'}>
							<React.Suspense fallback={null}>
								<VirtualServiceAgentHeader portalId={portalId ?? ''} />
							</React.Suspense>
						</ErrorBoundary>
					</NavItem>
				)}
				<NavItem>
					<ErrorBoundary packageName={'viewLayoutHeaderDumb'} id={'customizeMenu'}>
						<CustomiseMenu disableCustomiseMenu={disableAllHeaderLinks} />
					</ErrorBoundary>
				</NavItem>

				{isCSM && (
					<NavItem role="listitem">
						<ErrorBoundary packageName="viewLayoutHeaderDumb" id="CsmRequestsHeaderLink">
							<Box xcss={requestsLinkStyles}>
								<CsmRequestsHeaderLink />
							</Box>
						</ErrorBoundary>
					</NavItem>
				)}

				<NavItem>
					<AnalyticsContext data={{ actionSubjectId: 'navbarAvatar' }}>
						<AvatarMenuV2 disableAvatarMenu={disableAllHeaderLinks} />
					</AnalyticsContext>
				</NavItem>
			</>
		),
		[disableAllHeaderLinks, isCSM, portalId],
	);

	const unauthenticatedNavLinks = (
		<NavItem role="listitem">
			<ExternalNavLink>
				<LoginButton href={loginUrl ? loginUrl : defaultLoginUrl} actionSubjectId="navbarLogin">
					<FormattedMessage {...i18n.login} />
				</LoginButton>
			</ExternalNavLink>
		</NavItem>
	);

	const anonymousAccessNotEnbledAndUserNotLoggedIn = !(!!anonymousEnabled || !!userLoggedIn);

	let portalLinkUrl;
	let portalLinkAriaLabel;
	if (isAnonymous || anonymousAccessNotEnbledAndUserNotLoggedIn) {
		portalLinkUrl = defaultLoginUrl;
		portalLinkAriaLabel = formatMessage(i18n.helpCenter);
	} else {
		portalLinkUrl = getHelpCenterUrl();
		portalLinkAriaLabel = name ? name : formatMessage(i18n.helpCenter);
	}

	return (
		<AnalyticsContext data={{ source: 'navbar' }}>
			<Root
				showThemedBackground={Boolean(showThemedBackground)}
				headerBackgroundColor={headerBackgroundColor}
			>
				{showPortalLink && (
					<PortalLink
						to={portalLinkUrl}
						logoTextColor={logoTextColor}
						actionSubjectId="navbarHelpCenter"
						disabled={Boolean(disableAllHeaderLinks)}
						tabIndex={disableAllHeaderLinks ? -1 : undefined}
						aria-label={portalLinkAriaLabel}
					>
						<React.Suspense
							fallback={
								<UFOLoadHold name="header-dumb-logo-fallback">
									<Skeleton appearance="square" size="medium" />
								</UFOLoadHold>
							}
						>
							<LogoMemoizedHOC />
						</React.Suspense>
					</PortalLink>
				)}

				<Nav>
					<NavList role="list">
						{isUserLoaded && (
							<>
								{hideSearchButton || (
									<NavItem role="listitem">
										<Search trigger="icon" disableSearch={disableAllHeaderLinks} />
									</NavItem>
								)}

								{userLoggedIn ? authenticatedNavLinks : unauthenticatedNavLinks}
							</>
						)}
					</NavList>
				</Nav>
			</Root>
		</AnalyticsContext>
	);
};

export default Header;
// Nav line height set to 0 so it is correctly vertically aligned.
const NAV_LINE_HEIGHT = 0;
// eslint-disable-next-line @atlaskit/design-system/no-styled-tagged-template-expression, @atlaskit/ui-styling-standard/no-styled -- Disabled to rollout go/ui-styling-standard tooling, please resolve
const Root = styled.header<{ showThemedBackground: boolean; headerBackgroundColor?: string }>`
	display: flex;
	justify-content: space-between;
	align-items: center;
	${() => {
		if (isUseHelpCenterRefresh()) {
			return `
                padding-left: ${token('space.200', '16px')};
            `;
		}
		return `
            padding-left: ${token('space.100', '8px')};
            `;
	}}
	padding-right: ${token('space.100', '8px')};
	height: ${HEADER_HEIGHT}px;
	overflow: hidden;
	${(props) => {
		if (props.headerBackgroundColor) {
			return props.headerBackgroundColor ? `background-color: ${props.headerBackgroundColor}` : '';
		}
		return props.showThemedBackground ? `background-color: ${props.theme.color}` : '';
	}}
`;

// eslint-disable-next-line @atlaskit/design-system/no-styled-tagged-template-expression, @atlaskit/ui-styling-standard/no-styled -- Disabled to rollout go/ui-styling-standard tooling, please resolve,rulesdir/styled-component-order
const Nav = styled.nav`
	/* Ensures header icons are vertically aligned consistently accross browsers */
	line-height: ${NAV_LINE_HEIGHT};
	margin-left: auto;
`;

// eslint-disable-next-line @atlaskit/design-system/no-styled-tagged-template-expression, @atlaskit/ui-styling-standard/no-styled -- Disabled to rollout go/ui-styling-standard tooling, please resolve
const NavList = styled.div`
	margin: 0;
	padding: 0;
	display: flex;
	align-items: center;
`;

// eslint-disable-next-line @atlaskit/design-system/no-styled-tagged-template-expression, @atlaskit/ui-styling-standard/no-styled -- Disabled to rollout go/ui-styling-standard tooling, please resolve
const NavItem = styled.div`
	display: inline;
	margin: 0;
`;

// eslint-disable-next-line @atlaskit/design-system/no-styled-tagged-template-expression, @atlaskit/ui-styling-standard/no-styled -- Disabled to rollout go/ui-styling-standard tooling, please resolve
const LoginButton = styled((props: Omit<ButtonProps, 'theme'>) => <Button {...props} />)`
	&& {
		padding-left: ${token('space.150', '12px')};
		padding-right: ${token('space.150', '12px')};
	}
`;

// We need to override this because ADG button is transparent and not actually grey.
// This makes sure you can see the button against the banner.
// eslint-disable-next-line rulesdir/no-styled-export, @atlaskit/ui-styling-standard/no-exported-styles, @atlaskit/design-system/no-styled-tagged-template-expression, @atlaskit/ui-styling-standard/no-styled -- Disabled to rollout go/ui-styling-standard tooling, please resolve
export const ExternalNavLink = styled.div`
	${mixins.subtleThemeColorBackground};
	border: ${token('border.width.selected', '2px')} ${token('color.border.inverse', '#FFFFFF')} solid;
	border-radius: ${grid.multiple(2).px};
	margin-left: ${token('space.100', '8px')};
`;

interface PortalLinkProps {
	logoTextColor: 'banner' | 'theme';
	disabled?: boolean;
}

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- Disabled to rollout go/ui-styling-standard tooling, please resolve
const PortalLink = styled<React.ComponentProps<typeof Link> & PortalLinkProps>(
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Disabled to rollout go/ui-styling-standard tooling, please resolve
	({ logoTextColor, ...props }) => (
		// eslint-disable-next-line @atlassian/a11y/anchor-has-content -- Disabling existing violation, should be fixed when revisited.
		<Link {...props} />
	),
).attrs({
	className: 'cv-exclude-from-customisation',
})`
	/* stylelint-disable scale-unlimited/declaration-strict-value */
	color: ${(props) =>
		props.logoTextColor === 'banner'
			? props.theme.titleColor
			: token('color.link', props.theme.color)};

	&:hover {
		color: ${(props) =>
			props.logoTextColor === 'banner'
				? props.theme.titleColor
				: token('color.link', props.theme.color)};
	}
	pointer-events: ${(props) => (props.disabled ? 'none' : 'auto')};
`;

const requestsLinkStyles = xcss({
	marginInlineEnd: 'space.075',
});
